import {
  POLICY_REPLACEMENT_PROGRESSES,
  PolicyReplacementJSON,
  PolicyReplacementProgress,
  PolicyReplacementTargetJSON,
  UNRECOGNIZED,
} from "work/policy-replacements/_schemas/policy-replacement-schema";
import type { StatusTone } from "_shared/_views/tables/status-chip";

/** How the progress page names one state, and what it means for the agreement. */
export type PolicyReplacementProgressDescription = {
  label: string;
  meaning: string;
  /** The status chip's tone (#6713): a failure that needs a person is a warning, not an error. */
  tone: StatusTone;
};

/**
 * Each target state in the words of the design's "Batch states" table. The first six are the
 * worker's; In negotiation, Adopted, Not adopted and Declined are read from the revision itself.
 */
export const POLICY_REPLACEMENT_PROGRESS_DESCRIPTIONS: Record<PolicyReplacementProgress, PolicyReplacementProgressDescription> = {
  Pending: { label: "Pending", meaning: "Not processed yet, or due for another attempt.", tone: "neutral" },
  Waiting: {
    label: "Waiting",
    meaning: "A revision of this agreement is already open. The replacement goes on when that revision ends.",
    tone: "info",
  },
  NeedsAPerson: {
    label: "Needs a person",
    meaning: "Stopped for a reason another attempt cannot fix. Read the reason, then retry or finish the revision by hand.",
    tone: "warning",
  },
  Stopped: {
    label: "Stopped",
    meaning: "Failed too many times. Read the last error, then retry.",
    tone: "warning",
  },
  Withdrawn: { label: "Withdrawn", meaning: "Withdrawn before its revision was hired.", tone: "neutral" },
  AlreadyReplaced: {
    label: "Already replaced",
    meaning: "The agreement already holds a document that replaced the old policy. The newer document stays.",
    tone: "neutral",
  },
  InNegotiation: {
    label: "In negotiation",
    meaning: "The revision is with the vendor. The old policy governs until it is hired.",
    tone: "info",
  },
  Adopted: { label: "Adopted", meaning: "The revision was hired. The agreement holds the new policy.", tone: "success" },
  NotAdopted: {
    label: "Hired without the new policy",
    meaning: "The revision was hired, but the parties agreed to keep the old policy or another document.",
    tone: "warning",
  },
  Declined: {
    label: "Declined",
    meaning: "The revision was rejected or cancelled. The agreement keeps the old policy.",
    tone: "neutral",
  },
  [UNRECOGNIZED]: {
    label: "Not recognized",
    meaning: "This page does not recognize this state. Reload to update it.",
    tone: "neutral",
  },
};

/**
 * The states a client may withdraw from: anything whose revision is not yet hired and that is not
 * already over. A hired revision is never undone; a rollback is a new replacement.
 */
const WITHDRAWABLE: ReadonlySet<PolicyReplacementProgress> = new Set([
  "Pending",
  "Waiting",
  "NeedsAPerson",
  "Stopped",
  "InNegotiation",
]);

/** The states the worker gave up on, which the client may hand back to it. */
const RETRYABLE: ReadonlySet<PolicyReplacementProgress> = new Set(["NeedsAPerson", "Stopped"]);

/** The states that may still change on their own: the worker or the vendor has yet to act. */
const MOVING: ReadonlySet<PolicyReplacementProgress> = new Set(["Pending", "Waiting", "InNegotiation"]);

export type PolicyReplacementCount = { progress: PolicyReplacementProgress; count: number };

/**
 * One client policy replaced across many agreements, and where each agreement's part of it stands.
 *
 * The replacement has no state of its own: the page reads the count of targets in each state.
 */
export default class PolicyReplacement {
  public static fromJSON(json: PolicyReplacementJSON | null | undefined): PolicyReplacement | null {
    return json ? new PolicyReplacement(json) : null;
  }

  public static canWithdraw(target: PolicyReplacementTargetJSON): boolean {
    return WITHDRAWABLE.has(target.progress);
  }

  public static canRetry(target: PolicyReplacementTargetJSON): boolean {
    return RETRYABLE.has(target.progress);
  }

  public readonly id: string;
  public readonly replacedDocumentId: string;
  public readonly replacementDocumentId: string;
  public readonly reason: string;
  public readonly createdDate: string;
  public readonly targets: readonly PolicyReplacementTargetJSON[];

  /**
   * The number of targets in each state, in the order the states run, leaving out the empty ones.
   *
   * Read from the server's counts, which are keyed by state name. A target in a state this bundle
   * does not know is counted as not recognized, so the counts always add up to the rows shown.
   */
  public get counts(): PolicyReplacementCount[] {
    const known = new Map<string, number>(
      Object.entries(this.serverCounts).map(([name, count]) => [name.toLowerCase(), count]),
    );
    const counts: PolicyReplacementCount[] = POLICY_REPLACEMENT_PROGRESSES.map((progress) => ({
      progress,
      count: known.get(progress.toLowerCase()) ?? 0,
    }));
    const recognized = counts.reduce((total, entry) => total + entry.count, 0);
    const unrecognized = Math.max(0, this.targets.length - recognized);
    const others: PolicyReplacementCount = { progress: UNRECOGNIZED, count: unrecognized };
    return [...counts, others].filter((entry) => entry.count > 0);
  }

  /** Whether withdrawing the whole replacement would still change anything. */
  public get canWithdrawAny(): boolean {
    return this.targets.some(PolicyReplacement.canWithdraw);
  }

  /** Whether any target may still move without the client doing anything, so the page keeps asking. */
  public get isStillMoving(): boolean {
    return this.targets.some((target) => MOVING.has(target.progress));
  }

  public constructor(json: PolicyReplacementJSON) {
    this.id = json.id;
    this.replacedDocumentId = json.replacedDocumentId;
    this.replacementDocumentId = json.replacementDocumentId;
    this.reason = json.reason;
    this.createdDate = json.createdDate;
    this.targets = json.targets;
    this.serverCounts = json.counts;
  }

  private readonly serverCounts: Record<string, number>;
}
